import React, { useState, useEffect, useCallback } from "react";
import { Table, Input, Select, Button, Card, Space, Modal } from "antd";
import { CSVLink } from "react-csv";
import { EditOutlined } from '@ant-design/icons';
import axios from "axios";
import withRoleAccess from "../../hoc/withRoleAccess";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faSearch, faBookOpen } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import "./Classes.css";

const { Option } = Select;

const AllClassList = () => {
  const { t, i18n } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [editingTeacherId, setEditingTeacherId] = useState(null);
  const [editedTeacherId, setEditedTeacherId] = useState('');
  const [editingClassroomId, setEditingClassroomId] = useState(null);
  const [editedClassroomId, setEditedClassroomId] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [editingSubjectId, setEditingSubjectId] = useState(null);
  const [editedSubjectId, setEditedSubjectId] = useState('');
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  // Replace monthly picker with date range (Bangkok time)
  const [dateRange, setDateRange] = useState({
    startDate: moment().utcOffset('+07:00').startOf('month').format('YYYY-MM-DD'),
    endDate: moment().utcOffset('+07:00').endOf('month').format('YYYY-MM-DD')
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  // Add helper function for quick date range selection with Bangkok timezone (UTC+7)
  const setQuickDateRange = (range) => {
    // Create moment with Bangkok timezone
    const bangkokTime = moment().utcOffset('+07:00');
    
    switch(range) {
      case 'thisMonth':
        setDateRange({
          startDate: bangkokTime.clone().startOf('month').format('YYYY-MM-DD'),
          endDate: bangkokTime.clone().endOf('month').format('YYYY-MM-DD')
        });
        break;
      case 'nextMonth':
        setDateRange({
          startDate: bangkokTime.clone().add(1, 'month').startOf('month').format('YYYY-MM-DD'),
          endDate: bangkokTime.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD')
        });
        break;
      default:
        break;
    }
  };

const fetchClasses = useCallback(async () => {
  if (!dateRange?.startDate || !dateRange?.endDate) {
    console.warn("⚠️ Missing dateRange when calling fetchClasses");
    return;
  }

  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("❌ No token found");
      setLoading(false);
      return;
    }

    console.log("📡 Fetching classes for range:", dateRange);

    const response = await axios.get(`${API_URL}/classes/by-month`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      },
    });

    console.log("✅ Classes fetched:", response.data.length);
    setClasses(response.data);
  } catch (error) {
    console.error("❌ Error fetching classes:", error);
  } finally {
    setLoading(false);
  }
}, [API_URL, dateRange]);


const fetchTeachers = useCallback(async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/teachers`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setTeachers(response.data.filter((teacher) => teacher.status === "active"));
  } catch (error) {
    console.error("Error fetching teachers:", error);
  }
}, [API_URL]);

const fetchSubjects = useCallback(async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${API_URL}/subjects`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setSubjects(response.data);
  } catch (error) {
    console.error("Error fetching subjects:", error);
  }
}, [API_URL]);

useEffect(() => {
  fetchClasses();
  fetchTeachers();
  fetchSubjects(); 
}, [fetchClasses, fetchTeachers, fetchSubjects]);


  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classrooms`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
  
        const classroomsData = response.data || [];
  
        // Filter classrooms with availability 'true'
        const availableClassrooms = classroomsData.filter(classroom => classroom.availability === true);
  
        // Sort by 'number' field in alphanumeric order
        const sortedClassrooms = availableClassrooms.sort((a, b) => {
          return a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' });
        });
  
        setClassrooms(sortedClassrooms);
      } catch (error) {
        console.error('Error fetching classrooms:', error);
      }
    };
  
    fetchClassrooms();
  }, [API_URL]);

  const handleEditClassroomClick = (classId, classroomId) => {
    setEditingClassroomId(classId);
    setEditedClassroomId(classroomId); // Set initial classroom ID
  };

  const handleSaveClassroomClick = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      const updatedClass = {
        classroom_id: editedClassroomId,
      };

      await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setEditingClassroomId(null);
      setEditedClassroomId('');
      fetchClasses(); // Refresh the class list
    } catch (error) {
      console.error('Error updating classroom:', error);
    }
  };

    const handleSaveTeacherClick = (classId) => {
    showConfirmBeforeSave({
      title: "Confirm Teacher Update",
      content: "Are you sure you want to update the teacher?",
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          const updatedClass = {
            teacher_id: editedTeacherId,
          };
    
          await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
            headers: { Authorization: `Bearer ${token}` }
          });
    
          setEditingTeacherId(null);
          setEditedTeacherId('');
          fetchClasses();
        } catch (error) {
          console.error('Error updating teacher:', error);
        }
      }
    });
  };

    const handleSaveSubjectClick = (classId) => {
      showConfirmBeforeSave({
        title: "Confirm Subject Update",
        content: "Are you sure you want to update the subject?",
        onOk: async () => {
          try {
            const token = localStorage.getItem('token');
            const updatedClass = {
              subjectid: editedSubjectId,
            };

            await axios.put(`${API_URL}/classes/${classId}`, updatedClass, {
              headers: { Authorization: `Bearer ${token}` }
            });

            setEditingSubjectId(null);
            setEditedSubjectId('');
            fetchClasses();
          } catch (error) {
            console.error('Error updating subject:', error);
          }
        }
      });
    };

   const showConfirmBeforeSave = ({ onOk, title = "Are you sure?", content = "Do you want to save the changes?" }) => {
    Modal.confirm({
      title: title,
      content: content,
      okText: 'Yes',
      cancelText: 'No',
      onOk, // if user clicks yes, execute the onOk function
    });
  };

    const handleTeacherChangeDropdown = (value) => {
    setEditedTeacherId(value);
  };
  
    const handleEditTeacherClick = (classId, teacherId) => {
    setEditingTeacherId(classId);
    setEditedTeacherId(teacherId); 
  };

    const handleSubjectChangeDropdown = (value) => {
    setEditedSubjectId(value);
  };
  
  const handleEditSubjectClick = (classId, subjectId) => {
    setEditingSubjectId(classId);
    setEditedSubjectId(subjectId); 
  };

  // Updated filtered classes to use date range with Bangkok timezone
  const filteredClasses = classes
    .filter((classItem) => {
      // Convert class date to Bangkok time
      const classDate = moment(classItem.date).utcOffset('+07:00');
      const startDate = moment(dateRange.startDate).utcOffset('+07:00');
      const endDate = moment(dateRange.endDate).utcOffset('+07:00');
      
      const isInDateRange = 
        classDate.isSameOrAfter(startDate, 'day') && 
        classDate.isSameOrBefore(endDate, 'day');
      
      const isSearchMatch =
        classItem.subject_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classItem.class_code?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const isTeacherMatch =
        !selectedTeacher || classItem.teacher_id === selectedTeacher;

      return isInDateRange && isSearchMatch && isTeacherMatch;
    })
    .sort((a, b) => {
      // Sort by date and then by schedule_time
      const dateComparison = new Date(a.date) - new Date(b.date);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return moment(a.schedule_time, "HH:mm:ss").diff(
        moment(b.schedule_time, "HH:mm:ss")
      );
    });

  useEffect(() => {
  setPagination(prev => ({
    ...prev,
    total: classes.length
  }));
}, [classes]);


  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const getCurrentPageData = () => {
    const { current, pageSize } = pagination;
    const startIndex = (current - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClasses.slice(startIndex, endIndex);
  };

  // CSV Data for Export
  const csvData = filteredClasses.map((classItem) => ({
    Date: formatDate(classItem.date),
    Day: new Date(classItem.date).toLocaleDateString("en-US", { weekday: "long" }),
    "Class Code": classItem.class_code,
    Subject: classItem.subject_name,
    Tutor:
      teachers.find((teacher) => teacher.userid === classItem.teacher_id)?.nickname ||
      "N/A",
    "Start Time": formatTime(classItem.schedule_time),
    "End Time": classItem.end_time,
    Hours: classItem.schedule_hour,
    Room: classItem.classroom_number,
    Type: classItem.isprivate ? "Private" : "Group",
  }));

  const columns = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date) => moment(date).format("DD MMM YYYY"),
      fixed: "left",
    },
    {
      title: t("Day"),
      dataIndex: "day",
      key: "day",
      width: 100,
      render: (_, record) => (
        <span className="day-cell">
          {new Date(record.date).toLocaleDateString("en-US", { weekday: "long" })}
        </span>
      ),
    },
    {
      title: t("Time"),
      dataIndex: "time",
      key: "time",
      width: 120,
      render: (_, record) => (
        <span>
          {moment(record.schedule_time, "HH:mm:ss").format("HH:mm")} -{" "}
          {moment(record.end_time, "HH:mm:ss").format("HH:mm")}
        </span>
      ),
    },
    {
      title: t("Class Code"),
      dataIndex: "class_code",
      key: "class_code",
      width: 250,
      render: (text) => <div className="wrapped-cell">{text}</div>,
    },
        {
      title: t("Subject"),
      dataIndex: "subject_name",
      key: "subject_name",
      width: 250,
      render: (text, record) => {
        return editingSubjectId === record.classid && record.status !== 'approved' ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              value={editedSubjectId || undefined}
              onChange={handleSubjectChangeDropdown}
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {subjects.map((subject) => (
                <Option key={subject.subjectid} value={subject.subjectid}>
                  {subject.title}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveSubjectClick(record.classid)}
              >
                {t("Save")}
              </Button>
              <Button
                size="small"
                onClick={() => setEditingSubjectId(null)}
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Space>
        ) : (
          <Space>

            {record.status !== 'approved' && ( 
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEditSubjectClick(record.classid, record.subject_id)}
                style={{ padding: '0', height: 'auto' }}
              />
            )}
            <span>{text || t("N/A")}</span>
          </Space>
        );
      },
    },
   {
  title: t("Tutor"),
  dataIndex: "teacher_id",
  key: "teacher_id",
  width: 180,
  render: (teacherId, record) => {
    return editingTeacherId === record.classid && record.status !== 'approved' ? (
      <Space direction="vertical" style={{ width: '100%' }}>
        <Select
          value={editedTeacherId || undefined}
          onChange={handleTeacherChangeDropdown}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            option.label.toLowerCase().includes(input.toLowerCase())
          }
        >
          {[...teachers]
            .sort((a, b) =>
              `${a.nickname} ${a.firstname}`.localeCompare(`${b.nickname} ${b.firstname}`)
            )
            .map((teacher) => (
              <Option
                key={teacher.userid}
                value={teacher.userid}
                label={`${teacher.nickname} ${teacher.firstname}`}
              >
                {teacher.nickname} {teacher.firstname}
              </Option>
            ))}
        </Select>
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleSaveTeacherClick(record.classid)}
          >
            {t("Save")}
          </Button>
          <Button
            size="small"
            onClick={() => setEditingTeacherId(null)}
          >
            {t("Cancel")}
          </Button>
        </Space>
      </Space>
    ) : (
      <Space>
        <span>
          {teachers.find((teacher) => teacher.userid === teacherId)?.nickname || t("N/A")}
        </span>
        {record.status !== 'approved' && (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTeacherClick(record.classid, record.teacher_id)}
            style={{ padding: '0', height: 'auto' }}
          />
        )}
      </Space>
    );
  },
},
    {
      title: t("Room"),
      dataIndex: "classroom_number",
      key: "classroom_number",
      width: 150,
      align: "center",
      render: (_, record) => {
        return editingClassroomId === record.classid ? (
          <Space direction="vertical">
            <Select
              value={editedClassroomId || undefined}
              onChange={(value) => setEditedClassroomId(value)}
              style={{ width: '100%' }}
              showSearch
              placeholder="Select Room"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {classrooms.map((classroom) => (
                <Option key={classroom.classroomid} value={classroom.classroomid}>
                  {classroom.number}
                </Option>
              ))}
            </Select>
            <Space>
              <Button
                type="primary"
                size="small"
                onClick={() => handleSaveClassroomClick(record.classid)}
              >
                {t("Save")}
              </Button>
              <Button
                size="small"
                onClick={() => setEditingClassroomId(null)}
              >
                {t("Cancel")}
              </Button>
            </Space>
          </Space>
        ) : (
          <Space>
            <span>{record.classroom_number || t("N/A")}</span>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditClassroomClick(record.classid, record.classroom_id)}
            />
          </Space>
        );
      },
    },      
    {
      title: t("Type"),
      dataIndex: "isprivate",
      key: "isprivate",
      width: 90,
      align: "center",
      render: (isPrivate) => (
        <span
          style={{
            display: "inline-block",
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            lineHeight: 1,
            whiteSpace: "nowrap",
            background: isPrivate ? "#e6f4ff" : "#f6ffed",
            color: isPrivate ? "#1890ff" : "#52c41a",
            border: `1px solid ${isPrivate ? "#91caff" : "#b7eb8f"}`,
          }}
        >
          {isPrivate ? t("Private") : t("Group")}
        </span>
      ),
    },
  ];

  return (
    <Card className="class-list-container">
      <div className="cc-search-container">
        <Space size="middle" wrap>
          {/* Date Range picker */}
          <div className="date-range-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div>
              <label htmlFor="start-date" style={{ marginRight: '4px', fontSize: '14px' }}>
                {t("From")}:
              </label>
              <input
                id="start-date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label htmlFor="end-date" style={{ marginRight: '4px', fontSize: '14px' }}>
                {t("To")}:
              </label>
              <input
                id="end-date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                style={{
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Quick date range buttons - removed Today and This Week */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button size="small" onClick={() => setQuickDateRange('thisMonth')}>
              {t("This Month")}
            </Button>
            <Button size="small" onClick={() => setQuickDateRange('nextMonth')}>
              {t("Next Month")}
            </Button>
          </div>

          <Input
            prefix={
              <FontAwesomeIcon icon={faSearch} style={{ color: "#bfbfbf" }} />
            }
            placeholder={t("Search by subject or class code")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />

          <Select
            showSearch
            placeholder={t("Filter by teacher")}
            value={selectedTeacher}
            onChange={(value) => setSelectedTeacher(value)}
            style={{ width: 200 }}
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          >
            <Option value="" label={t("All Teachers")}>{t("All Teachers")}</Option>
            {teachers.map((teacher) => (
              <Option key={teacher.userid} value={teacher.userid} label={`${teacher.nickname} ${teacher.firstname}`}>
                {teacher.nickname} {teacher.firstname}
              </Option>
            ))}
          </Select>

          <CSVLink
            data={csvData}
            filename={`class_list_${dateRange.startDate}_to_${dateRange.endDate}.csv`}
          >
            <Button
              type="primary"
              className="export-button"
              icon={<FontAwesomeIcon icon={faFileExcel} />}
            >
              {t("Export CSV")}
            </Button>
          </CSVLink>
        </Space>
      </div>

      <Table
        dataSource={getCurrentPageData()}
        columns={columns}
        rowKey="classid"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: filteredClasses.length,
          showSizeChanger: true,
          pageSizeOptions: ["50", "100", "200"],
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} items`
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        size="middle"
      />
    </Card>
  );
};

export default withRoleAccess(AllClassList, ["admin", "superadmin"]);